import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { CheckCircle, CalendarBlank, ArrowRight, Hourglass, XCircle } from "@phosphor-icons/react";

export default function ReserveSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: null, calendar_url: null, meeting_url: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: null, calendar_url: null, meeting_url: null });
      return;
    }

    let cancelled = false;
    const MAX_ATTEMPTS = 10;
    const POLL_MS = 2000;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/reserve/status/${sessionId}`);
        if (cancelled) return;
        setState({
          status: r.data.status || "checking",
          payment_status: r.data.payment_status,
          calendar_url: r.data.calendar_url,
          meeting_url: r.data.meeting_url,
        });
        if (r.data.payment_status === "paid") return;
        if (r.data.status === "expired") return;
        if (attemptsRef.current < MAX_ATTEMPTS) setTimeout(poll, POLL_MS);
        else setState((s) => ({ ...s, status: "timeout" }));
      } catch {
        if (!cancelled && attemptsRef.current < MAX_ATTEMPTS) setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const isPaid = state.payment_status === "paid";
  const isError = !sessionId || state.status === "expired" || state.status === "error";

  return (
    <div data-testid="reserve-success-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <section className="max-w-3xl mx-auto px-6 lg:px-12 pt-44 pb-32">
        {isPaid ? (
          <div className="card-surface p-10 lg:p-14 text-center" data-testid="reserve-success-paid">
            <CheckCircle size={48} weight="thin" className="text-exodus-emerald mx-auto" />
            <p className="eyebrow mt-6">Deposit Received</p>
            <h1 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
              You're <span className="gold-text italic">reserved</span>.
            </h1>
            <p className="text-exodus-muted mt-6 leading-relaxed max-w-md mx-auto">
              Two options below — pick a time on Leon's calendar (recommended), or jump straight into the Google Meet room.
              You'll get a confirmation email either way.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
              <a
                href={state.calendar_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-calendar-button"
                className="btn-gold inline-flex"
              >
                <CalendarBlank size={18} weight="bold" /> Pick a time
                <ArrowRight size={16} weight="bold" />
              </a>
              {state.meeting_url && (
                <a
                  href={state.meeting_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-meet-button"
                  className="inline-flex items-center gap-2 px-6 py-4 text-sm border border-exodus-gold text-exodus-gold hover:bg-exodus-gold hover:text-exodus-bg transition-colors"
                >
                  Join Google Meet now
                </a>
              )}
            </div>
            <p className="text-xs text-exodus-muted mt-6">
              Can't open them? Check your email — we sent both links there too.
            </p>
          </div>
        ) : isError ? (
          <div className="card-surface p-10 lg:p-14 text-center" data-testid="reserve-success-error">
            <XCircle size={48} weight="thin" className="text-exodus-oxblood mx-auto" />
            <p className="eyebrow mt-6">Payment not detected</p>
            <h1 className="font-display text-4xl mt-4">Looks like the checkout didn't complete.</h1>
            <p className="text-exodus-muted mt-6 max-w-md mx-auto">
              No charge was made. You can try the reservation again — your information is still on file.
            </p>
            <Link to="/reserve" className="btn-gold mt-10" data-testid="retry-reserve">Try again</Link>
          </div>
        ) : (
          <div className="card-surface p-10 lg:p-14 text-center" data-testid="reserve-success-pending">
            <Hourglass size={48} weight="thin" className="text-exodus-gold mx-auto animate-pulse" />
            <p className="eyebrow mt-6">Confirming your deposit</p>
            <h1 className="font-display text-4xl mt-4">Hold tight — just verifying with the payment processor.</h1>
            <p className="text-exodus-muted mt-6">This usually takes 5–10 seconds.</p>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
